import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import {
  Banner,
  Button,
  Dialog,
  FormRow,
  Segmented,
  Select,
  Skeleton,
  TextField,
  TextLink,
} from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { createDeal, useCreateOptions } from '../api';
import type { CreateDealBody, DealRecord } from '../types';
import styles from './dialogs.module.css';

/**
 * Deals 03/04 „Deal anlegen": the contact is set, the unit is chosen or left open, closer/setter and
 * the start stage come from `GET /deals/create-options`; `POST /deals` answers with the record. 409
 * texts („Der Kontakt hat bereits einen aktiven Deal.", „Die Einheit ist nicht mehr verfügbar.") are
 * shown as banners.
 */

export interface CreateDealDialogProps {
  open: boolean;
  contactId: string | null;
  onClose: () => void;
  onCreated: (deal: DealRecord) => void;
  onOpenDeal: (dealId: string) => void;
}

type UnitMode = 'unit' | 'open';

export function CreateDealDialog({ open, contactId, onClose, onCreated, onOpenDeal }: CreateDealDialogProps) {
  const options = useCreateOptions(open ? contactId : null);
  const formId = useId();
  const [mode, setMode] = useState<UnitMode>('unit');
  const [propertyId, setPropertyId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [closerId, setCloserId] = useState('');
  const [setterId, setSetterId] = useState('');
  const [stageId, setStageId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const data = options.data;

  useEffect(() => {
    if (!open) return;
    setMode('unit');
    setPropertyId('');
    setUnitId('');
    setErrors({});
    setBanner(null);
  }, [open, contactId]);

  useEffect(() => {
    if (!data) return;
    setCloserId((current) => current || data.defaultCloser?.id || data.closers[0]?.id || '');
    setSetterId((current) => current || data.defaultSetter?.id || '');
    setStageId((current) => current || data.defaultStageId);
    setPropertyId((current) => current || data.properties[0]?.id || '');
  }, [data]);

  const property = useMemo(
    () => data?.properties.find((entry) => entry.id === propertyId) ?? null,
    [data, propertyId],
  );
  const units = property?.units ?? [];
  const unit = units.find((entry) => entry.id === unitId) ?? null;
  const hasActiveDeal = data?.contact.activeDealId ?? null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!data || !contactId || pending) return;
    const next: Record<string, string> = {};
    if (mode === 'unit' && unitId === '') next['unitId'] = 'Bitte wählen Sie eine Einheit.';
    if (closerId === '') next['closerId'] = 'Bitte wählen Sie einen Closer.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const body: CreateDealBody = { contactId, closerId, stageId };
    if (setterId !== '') body.setterId = setterId;
    if (mode === 'unit') body.unitId = unitId;
    else if (propertyId !== '') body.propertyId = propertyId;
    setPending(true);
    setBanner(null);
    try {
      const deal = await createDeal(body);
      onCreated(deal);
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && Object.keys(error.fields).length > 0) {
        setErrors(error.fields);
      } else {
        setBanner(errorMessage(error));
      }
    } finally {
      setPending(false);
    }
  }

  const title = data
    ? mode === 'unit'
      ? `Deal für ${data.contact.name}`
      : 'Deal ohne Objekt'
    : 'Deal anlegen';
  const footerNote =
    mode === 'unit' ? 'Einheit bleibt frei, bis reserviert wird' : 'Lifecycle wechselt auf „Kunde“';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={600}
      eyebrow="Deal anlegen"
      title={title}
      subtitle="Aus der Kontaktakte · Kontakt ist gesetzt."
      initialFocus="container"
      footerNote={footerNote}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending} disabled={!data || hasActiveDeal !== null}>
            Deal anlegen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.createForm} onSubmit={submit} noValidate>
        {contactId === null ? (
          <Banner tone="warning">
            Ein Deal wird aus der Kontaktakte angelegt: Der Kontakt ist dort gesetzt.
          </Banner>
        ) : null}
        {options.isPending && contactId !== null ? (
          <>
            <Skeleton variant="block" height={40} />
            <Skeleton variant="block" height={40} />
            <Skeleton variant="block" height={40} />
          </>
        ) : null}
        {options.isError ? <Banner tone="danger">{errorMessage(options.error)}</Banner> : null}
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {data ? (
          <>
            {hasActiveDeal ? (
              <Banner
                tone="warning"
                actions={
                  <TextLink as="button" onClick={() => onOpenDeal(hasActiveDeal)}>
                    Deal öffnen
                  </TextLink>
                }
              >
                Der Kontakt hat bereits einen aktiven Deal.
              </Banner>
            ) : null}
            <TextField label="Kontakt" value={data.contact.name} readOnly />
            <div>
              <div className={styles.fieldHead} style={{ marginBottom: 8 }}>
                <span className={styles.fieldLabel} style={{ fontSize: 12 }}>
                  Objekt
                </span>
              </div>
              <Segmented
                label="Objekt"
                value={mode}
                items={[
                  { value: 'unit', label: 'Einheit wählen' },
                  { value: 'open', label: 'Noch offen' },
                ]}
                onChange={(value) => {
                  setMode(value);
                  setErrors({});
                }}
              />
            </div>
            {mode === 'unit' ? (
              <FormRow columns={2}>
                <Select
                  label="Objekt"
                  value={propertyId}
                  options={data.properties.map((entry) => ({ value: entry.id, label: entry.name }))}
                  placeholder={data.properties.length === 0 ? 'Kein Objekt im Vertrieb' : undefined}
                  onChange={(event) => {
                    setPropertyId(event.target.value);
                    setUnitId('');
                  }}
                />
                <Select
                  label="Einheit"
                  value={unitId}
                  placeholder="Bitte wählen"
                  error={errors['unitId']}
                  labelEnd={
                    unit ? <span className={styles.unitStatus}>{unit.availabilityLabel}</span> : undefined
                  }
                  options={units.map((entry) => ({
                    value: entry.id,
                    label:
                      entry.availability === 'available'
                        ? entry.label
                        : `${entry.label} · ${entry.availabilityLabel}`,
                    disabled: entry.availability !== 'available',
                  }))}
                  onChange={(event) => setUnitId(event.target.value)}
                />
              </FormRow>
            ) : (
              <>
                <div className={styles.createHintBox}>
                  Der Deal startet in „Beratung“ ohne Einheit. Für „Reserviert“ muss eine Einheit gewählt
                  sein.
                </div>
                <Select
                  label="Objekt"
                  optional
                  value={propertyId}
                  placeholder="Noch offen"
                  options={data.properties.map((entry) => ({ value: entry.id, label: entry.name }))}
                  onChange={(event) => setPropertyId(event.target.value)}
                />
              </>
            )}
            <FormRow columns={2}>
              <Select
                label="Closer"
                value={closerId}
                error={errors['closerId']}
                options={data.closers.map((entry) => ({ value: entry.id, label: entry.shortName }))}
                onChange={(event) => setCloserId(event.target.value)}
              />
              <Select
                label="Setter"
                value={setterId}
                placeholder="Kein Setter"
                hint={
                  data.defaultSetter && setterId === data.defaultSetter.id
                    ? 'aus dem gebuchten Termin'
                    : undefined
                }
                error={errors['setterId']}
                options={data.setters.map((entry) => ({ value: entry.id, label: entry.shortName }))}
                onChange={(event) => setSetterId(event.target.value)}
              />
            </FormRow>
            <Select
              label="Start-Stage"
              value={stageId}
              hint={data.laterStageHint}
              error={errors['stageId']}
              options={data.stages.map((entry) => ({ value: entry.id, label: entry.name }))}
              onChange={(event) => setStageId(event.target.value)}
            />
          </>
        ) : null}
      </form>
    </Dialog>
  );
}
